/**
 * Worker Onboarding Constants
 * 
 * This file contains all constants used in the worker onboarding process.
 * Following DRY and SOLID principles - single source of truth for all configuration values.
 */

// ========== Nationalities List ==========
export const NATIONALITIES = [
  "Afghan",
  "Albanian",
  "Algerian",
  "American",
  "Andorran",
  "Angolan",
  "Anguillan",
  "Argentine",
  "Armenian",
  "Australian",
  "Austrian",
  "Azerbaijani",
  "Bahamian",
  "Bahraini",
  "Bangladeshi",
  "Barbadian",
  "Belarusian",
  "Belgian",
  "Belizean",
  "Beninese",
  "Bermudian",
  "Bhutanese",
  "Bolivian",
  "Botswanan",
  "Brazilian",
  "British",
  "British Virgin Islander",
  "Bruneian",
  "Bulgarian",
  "Burkinan",
  "Burmese",
  "Burundian",
  "Cambodian",
  "Cameroonian",
  "Canadian",
  "Cape Verdean",
  "Cayman Islander",
  "Central African",
  "Chadian",
  "Chilean",
  "Chinese",
  "Citizen of Antigua and Barbuda",
  "Citizen of Bosnia and Herzegovina",
  "Citizen of Guinea-Bissau",
  "Citizen of Kiribati",
  "Citizen of Seychelles",
  "Citizen of the Dominican Republic",
  "Citizen of Vanuatu",
  "Colombian",
  "Comoran",
  "Congolese (Congo)",
  "Congolese (DRC)",
  "Cook Islander",
  "Costa Rican",
  "Croatian",
  "Cuban",
  "Cymraes",
  "Cymro",
  "Cypriot",
  "Czech",
  "Danish",
  "Djiboutian",
  "Dominican",
  "Dutch",
  "East Timorese",
  "Ecuadorean",
  "Egyptian",
  "Emirati",
  "English",
  "Equatorial Guinean",
  "Eritrean",
  "Estonian",
  "Ethiopian",
  "Faroese",
  "Fijian",
  "Filipino",
  "Finnish",
  "French",
  "Gabonese",
  "Gambian",
  "Georgian",
  "German",
  "Ghanaian",
  "Gibraltarian",
  "Greek",
  "Greenlandic",
  "Grenadian",
  "Guamanian",
  "Guatemalan",
  "Guinean",
  "Guyanese",
  "Haitian",
  "Honduran",
  "Hong Konger",
  "Hungarian",
  "Icelandic",
  "Indian",
  "Indonesian",
  "Iranian",
  "Iraqi",
  "Irish",
  "Israeli",
  "Italian",
  "Ivorian",
  "Jamaican",
  "Japanese",
  "Jordanian",
  "Kazakh",
  "Kenyan",
  "Kittitian",
  "Kosovan",
  "Kuwaiti",
  "Kyrgyz",
  "Lao",
  "Latvian",
  "Lebanese",
  "Liberian",
  "Libyan",
  "Liechtenstein citizen",
  "Lithuanian",
  "Luxembourger",
  "Macanese",
  "Macedonian",
  "Malagasy",
  "Malawian",
  "Malaysian",
  "Maldivian",
  "Malian",
  "Maltese",
  "Marshallese",
  "Martiniquais",
  "Mauritanian",
  "Mauritian",
  "Mexican",
  "Micronesian",
  "Moldovan",
  "Monegasque",
  "Mongolian",
  "Montenegrin",
  "Montserratian",
  "Moroccan",
  "Mosotho",
  "Mozambican",
  "Namibian",
  "Nauruan",
  "Nepalese",
  "New Zealander",
  "Nicaraguan",
  "Nigerian",
  "Nigerien",
  "Niuean",
  "North Korean",
  "Northern Irish",
  "Norwegian",
  "Omani",
  "Pakistani",
  "Palauan",
  "Palestinian",
  "Panamanian",
  "Papua New Guinean",
  "Paraguayan",
  "Peruvian",
  "Pitcairn Islander",
  "Polish",
  "Portuguese",
  "Prydeinig",
  "Puerto Rican",
  "Qatari",
  "Romanian",
  "Russian",
  "Rwandan",
  "Salvadorean",
  "Sammarinese",
  "Samoan",
  "Sao Tomean",
  "Saudi Arabian",
  "Scottish",
  "Senegalese",
  "Serbian",
  "Sierra Leonean",
  "Singaporean",
  "Slovak",
  "Slovenian",
  "Solomon Islander",
  "Somali",
  "South African",
  "South Korean",
  "South Sudanese",
  "Spanish",
  "Sri Lankan",
  "St Helenian",
  "St Lucian",
  "Stateless",
  "Sudanese",
  "Surinamese",
  "Swazi",
  "Swedish",
  "Swiss",
  "Syrian",
  "Taiwanese",
  "Tajik",
  "Tanzanian",
  "Thai",
  "Togolese",
  "Tongan",
  "Trinidadian",
  "Tristanian",
  "Tunisian",
  "Turkish",
  "Turkmen",
  "Turks and Caicos Islander",
  "Tuvaluan",
  "Ugandan",
  "Ukrainian",
  "Uruguayan",
  "Uzbek",
  "Vatican citizen",
  "Venezuelan",
  "Vietnamese",
  "Vincentian",
  "Wallisian",
  "Welsh",
  "Yemeni",
  "Zambian",
  "Zimbabwean",
];

/**
 * Convert nationalities array to react-select options format
 * @returns {Array} Array of {value, label} objects
 */
export const getNationalityOptions = () =>
  NATIONALITIES.map((nationality) => ({
    value: nationality,
    label: nationality,
  }));

// ========== Countries List (address / residence) ==========
export const UK_COUNTRY = "United Kingdom";

export const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina",
  "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados",
  "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana",
  "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon",
  "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo",
  "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica",
  "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia",
  "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany",
  "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti",
  "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel",
  "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kosovo", "Kuwait",
  "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein",
  "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta",
  "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco",
  "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal",
  "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia",
  "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay",
  "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda",
  "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino",
  "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone",
  "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea",
  "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
  "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago",
  "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates",
  "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela",
  "Vietnam", "Yemen", "Zambia", "Zimbabwe"
].sort();

/**
 * Convert countries array to react-select options format
 * @returns {Array} Array of {value, label} objects
 */
export const getCountryOptions = () => {
  return COUNTRIES.map((country) => ({
    value: country,
    label: country
  }));
};

// ========== Work Preferences Options ==========
export const AVAILABILITY_OPTIONS = ["weekdays", "weekend", "nights", "bankHoliday", "urgentCover"];

export const PROFILE_AVAILABILITY_STATUS = {
  AVAILABLE: "available",
  UNAVAILABLE: "unavailable",
};

export const PROFILE_AVAILABILITY_STATUS_OPTIONS = [
  PROFILE_AVAILABILITY_STATUS.AVAILABLE,
  PROFILE_AVAILABILITY_STATUS.UNAVAILABLE,
];

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English", countryCode: "GB", localeFolder: "eng" },
  { code: "pl", label: "Polish", countryCode: "PL", localeFolder: "pl" },
  { code: "ro", label: "Romanian", countryCode: "RO", localeFolder: "ro" },
  { code: "pt", label: "Portuguese", countryCode: "PT", localeFolder: "pt" },
  { code: "sp", label: "Spanish", countryCode: "ES", localeFolder: "sp" },
  { code: "it", label: "Italian", countryCode: "IT", localeFolder: "it" },
  { code: "fr", label: "French", countryCode: "FR", localeFolder: "fr" },
  { code: "gr", label: "German", countryCode: "DE", localeFolder: "gr" },
  { code: "nl", label: "Dutch", countryCode: "NL", localeFolder: "nl" },
  { code: "lt", label: "Lithuanian", countryCode: "LT", localeFolder: "lt" },
  { code: "lv", label: "Latvian", countryCode: "LV", localeFolder: "lv" },
  { code: "bg", label: "Bulgarian", countryCode: "BG", localeFolder: "bg" },
  { code: "hu", label: "Hungarian", countryCode: "HU", localeFolder: "hu" },
  { code: "sq", label: "Albanian", countryCode: "AL", localeFolder: "sq" },
  { code: "tr", label: "Turkish", countryCode: "TR", localeFolder: "tr" },
  { code: "rs", label: "Russian", countryCode: "RU", localeFolder: "rs" },
  { code: "uk", label: "Ukrainian", countryCode: "UA", localeFolder: "uk" },
  { code: "ar", label: "Arabic", countryCode: "SA", localeFolder: "ar" },
  { code: "ku", label: "Kurdish", countryCode: "IQ", localeFolder: "ku" },
  { code: "fa", label: "Farsi", countryCode: "IR", localeFolder: "fa" },
  { code: "ps", label: "Pashto", countryCode: "AF", localeFolder: "ps" },
  { code: "hi", label: "Hindi", countryCode: "IN", localeFolder: "hi" },
  { code: "ur", label: "Urdu", countryCode: "PK", localeFolder: "ur" },
  { code: "pa", label: "Punjabi", countryCode: "IN", localeFolder: "pa" },
  { code: "bn", label: "Bengali", countryCode: "BD", localeFolder: "bn" },
  { code: "gu", label: "Gujarati", countryCode: "IN", localeFolder: "gu" },
  { code: "ta", label: "Tamil", countryCode: "LK", localeFolder: "ta" },
  { code: "zh", label: "Mandarin Chinese", countryCode: "CN", localeFolder: "zh" },
  { code: "vi", label: "Vietnamese", countryCode: "VN", localeFolder: "vi" },
  { code: "th", label: "Thai", countryCode: "TH", localeFolder: "th" },
  { code: "so", label: "Somalian", countryCode: "SO", localeFolder: "so" },
];

/** localeFolder values — each must have src/locales/{folder}/translation.json */
export const LOCALE_FOLDERS = SUPPORTED_LANGUAGES.map((lang) => lang.localeFolder);

export const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES.map((lang) => lang.label);

/**
 * Convert languages array to react-select options format
 * @returns {Array} Array of {value, label} objects
 */
export const getLanguageOptions = () =>
  LANGUAGE_OPTIONS.map((lang) => ({
    value: lang,
    label: lang,
  }));

/**
 * App UI language picker metadata (i18n code → label + flag)
 * @returns {Record<string, { label: string, countryCode: string }>}
 */
export const getAppLanguagesMap = () =>
  Object.fromEntries(
    SUPPORTED_LANGUAGES.map(({ code, label, countryCode }) => [
      code,
      { label, countryCode },
    ])
  );

export const TRANSPORT_OPTIONS = ["car", "bike", "public", "walk"];

/** API expects at least one; values must match backend enum exactly */
export const WHAT_MATTERS_MOST_OPTIONS = [
  "Fair pay",
  "Consistent opportunities",
  "Local work",
  "Career growth and training",
  "Flexibility and work-life balance",
  "Safe and inclusive workplaces"
];

export function normalizeWhatMattersMost(value) {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (v) => typeof v === "string" && WHAT_MATTERS_MOST_OPTIONS.includes(v)
  );
}

export const PREFERRED_WORK_RADIUS_OPTIONS = [5, 10, 20, 30];

// ========== File Validation Constants ==========
export const FILE_VALIDATION = {
  MAX_MB: 3, // All files except selfie
  SELFIE_MAX_MB: 5, // Upload selfie only
  ONSCREENING_VIDEO_MAX_MB: 50,
  BYTES_IN_MB: 1024 * 1024,
  MAX_FILES_PER_FIELD: 2,
  ALLOWED_EXTENSIONS: /\.(pdf|jpe?g|png|webp|doc|docx)$/i,
  ALLOWED_PDF_EXTENSION: /\.pdf$/i,
  ALLOWED_IMAGE_EXTENSIONS: /\.(jpe?g|png|webp)$/i,
  ALLOWED_VIDEO_EXTENSIONS: /\.(webm|mp4|mov)$/i,
};

// ========== Validation Regex Patterns ==========
export const VALIDATION_PATTERNS = {
  SORT_CODE: /^\d{2}-\d{2}-\d{2}$/,
  UK_VAT: /^GB\d{9}$|^GB\d{12}$|^GBGD\d{3}$|^GBHA\d{3}$/,
  NATIONAL_INSURANCE: /^(?!BG|GB|NK|KN|TN|NT|ZZ)[A-CEGHJ-PR-TW-Z]{2}\s?\d{2}\s?\d{2}\s?\d{2}\s?[A-D]$/i,
  ACCOUNT_NUMBER: /^\d+$/,
  PHONE_NUMBER: /^[0-9]+$/
};

// ========== Student Loan Plan Types ==========
export const STUDENT_LOAN_PLANS = [1, 2, 3, 4, 5];

/** API values for employmentTax.planType */
export const STUDENT_LOAN_PLAN_TYPES = {
  PLAN_ONE: "plan_one",
  PLAN_TWO: "plan_two",
  PLAN_THREE: "plan_three",
  PLAN_FOUR: "plan_four",
  PLAN_FIVE: "plan_five",
  POSTGRADUATE_LOAN: "postgraduate_loan",
};

export const STUDENT_LOAN_PLAN_OPTIONS = [
  { value: STUDENT_LOAN_PLAN_TYPES.PLAN_ONE, label: "Plan 1" },
  { value: STUDENT_LOAN_PLAN_TYPES.PLAN_TWO, label: "Plan 2" },
  { value: STUDENT_LOAN_PLAN_TYPES.PLAN_THREE, label: "Plan 3" },
  { value: STUDENT_LOAN_PLAN_TYPES.PLAN_FOUR, label: "Plan 4" },
  { value: STUDENT_LOAN_PLAN_TYPES.PLAN_FIVE, label: "Plan 5" },
  {
    value: STUDENT_LOAN_PLAN_TYPES.POSTGRADUATE_LOAN,
    label: "Postgraduate loan",
  },
];

// ========== Starter Checklist Choices ==========
export const STARTER_CHECKLIST_CHOICES = {
  FIRST_JOB_SINCE_6_APR: "first_job_since_6_apr",
  HAD_ANOTHER_JOB_NO_P45: "had_another_job_no_p45",
  HAVE_ANOTHER_JOB_OR_PENSION: "have_another_job_or_pension"
};

export const STARTER_CHECKLIST_OPTIONS = [
  {
    value: STARTER_CHECKLIST_CHOICES.FIRST_JOB_SINCE_6_APR,
    intro:
      "This is my first job since 6 April and since the 6 April I have not received payments from any of the following:",
    bullets: [
      "Jobseeker's Allowance",
      "Employment and Support Allowance",
      "Incapacity Benefit",
    ],
  },
  {
    value: STARTER_CHECKLIST_CHOICES.HAD_ANOTHER_JOB_NO_P45,
    intro:
      "Since 6 April I have had another job but I do not have a P45. And/or since the 6 April I have received payments from any of the following:",
    bullets: [
      "Jobseeker's Allowance",
      "Employment and Support Allowance",
      "Incapacity Benefit",
    ],
  },
  {
    value: STARTER_CHECKLIST_CHOICES.HAVE_ANOTHER_JOB_OR_PENSION,
    intro:
      "I have another job and/or I am in receipt of a State, workplace or private pension.",
    bullets: [],
  },
];

export function getStarterChecklistOption(value) {
  const key = String(value ?? "").trim();
  if (!key) return null;
  return STARTER_CHECKLIST_OPTIONS.find((option) => option.value === key) || null;
}

export function labelStarterChecklistChoice(value) {
  const option = getStarterChecklistOption(value);
  if (!option) return String(value ?? "").trim();
  if (!option.bullets?.length) return option.intro;
  return `${option.intro} ${option.bullets.join(", ")}`;
}

export function labelStudentLoanPlanType(value) {
  if (value == null || String(value).trim() === "") return "";
  const key = String(value).trim();
  const match = STUDENT_LOAN_PLAN_OPTIONS.find((option) => option.value === key);
  return match?.label || key;
}

// ========== Gender Options ==========
export const GENDER_OPTIONS = {
  MALE: "male",
  FEMALE: "female",
  OTHER: "other"
};

// ========== Right to Work Status ==========
/** When isBritishOrIrish = true */
export const RIGHT_TO_WORK_STATUS_BRITISH_IRISH = "British or Irish";

/** When isBritishOrIrish = false – user must pick one */
export const RIGHT_TO_WORK_STATUS_STUDENT_VISA = "Student Visa";

export const RIGHT_TO_WORK_STATUS_OPTIONS_NON_BRITISH = [
  "Non British or Irish",
  RIGHT_TO_WORK_STATUS_STUDENT_VISA,
  "Other"
];

/** Shown after user confirms legal right to work in the UK (yes) — values must match API */
export const RIGHT_TO_WORK_STATUS_OPTIONS_ALL = [
  RIGHT_TO_WORK_STATUS_BRITISH_IRISH,
  ...RIGHT_TO_WORK_STATUS_OPTIONS_NON_BRITISH,
];

/** Emergency contact relationship — value stored in API */
export const EMERGENCY_RELATIONSHIP_OPTIONS = [
  { value: "Parent", labelKey: "emergencyRelationshipParent" },
  { value: "Spouse", labelKey: "emergencyRelationshipSpouse" },
  { value: "Sibling", labelKey: "emergencyRelationshipSibling" },
  { value: "Child", labelKey: "emergencyRelationshipChild" },
  { value: "Friend", labelKey: "emergencyRelationshipFriend" },
  { value: "Partner", labelKey: "emergencyRelationshipPartner" },
  { value: "Other", labelKey: "emergencyRelationshipOther" }
];

export const JOBARK_TERMS_URL = "https://www.jobark.co.uk/terms-of-use/";
export const JOBARK_PRIVACY_URL = "https://www.jobark.co.uk/privacy-policy/";
export const WORKER_ENGAGEMENT_AGREEMENT_PATH =
  "/worker-engagement-agreement";

export const UK_HOME_OFFICE_SHARE_CODE_URL =
  "https://www.gov.uk/view-prove-immigration-status";
export const UK_DVLA_SHARE_CODE_URL =
  "https://www.gov.uk/check-driving-information";

/** DVLA — validate driving record (admin worker details) */
export const UK_DVLA_DRIVING_RECORD_VALIDATE_URL =
  "https://www.viewdrivingrecord.service.gov.uk/lang/en/driving-record/validate";

/** DBS Update Service — GOV.UK overview (worker onboarding) */
export const UK_DBS_UPDATE_SERVICE_GOV_UK_URL =
  "https://www.gov.uk/dbs-update-service";

/** DBS Update Service — certificate status checker */
export const UK_DBS_UPDATE_SERVICE_URL =
  "https://secure.crbonline.gov.uk/crsc/check?execution=e1s1";

/** CSCS card verification (admin worker details) */
export const CSCS_SMART_CHECK_URL = "https://cscssmartcheck.co.uk/";

/** Admin portals — worker details right-to-work verification */
export const TRUSTID_ADMIN_PORTAL_URL =
  "https://cloud.trustid.co.uk/vpe/webclient/#/";
export const UK_CHECK_IMMIGRATION_STATUS_URL =
  "https://www.gov.uk/check-immigration-status";
export const UK_VIEW_RIGHT_TO_WORK_URL =
  "https://www.gov.uk/view-right-to-work";

/** i18n keys for acceptable proof-of-address document labels (worker onboarding + admin). */
export const ACCEPTABLE_PROOF_OF_ADDRESS_DOCUMENT_KEYS = [
  "proofOfAddressAcceptableUtilityBill",
  "proofOfAddressAcceptableBankStatement",
  "proofOfAddressAcceptableCouncilTaxBill",
  "proofOfAddressAcceptableHmrcLetter",
  "proofOfAddressAcceptableNhsLetter",
  "proofOfAddressAcceptableTenancyAgreement",
];

export const WORKER_EXPERIENCE_LEVEL_OPTIONS = [
  "New to Sector",
  "Developing Experience",
  "Competent",
  "Highly Experienced",
];

